import { faUser } from "@fortawesome/free-regular-svg-icons";
import { faSignOutAlt, faUserDoctor } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LOGO from "assets/images/profices.png";
import { AlertState } from "global/AlertState";
import { LoginActiveView } from "global/LoginActiveView";
import { SearchState } from "global/Search";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import callApi from "services/api";
import "styles/css/header.css";
import { LoginState } from "../global/LoginState";
import { RoleState } from "../global/RoleState";
import { UserState } from "../global/UserState";
import { MenuItems } from "./MenuItems";
import Cookies from "js-cookie";

const Header = () => {
  const navigate = useNavigate();
  const [loginState, setloginState] = useRecoilState(LoginState);
  const [roleState, setRoleState] = useRecoilState(RoleState);
  const [, setAlertMessage] = useRecoilState(AlertState);
  const [user] = useRecoilState(UserState);
  const [menu, setMenu] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [, setLoginActiveView] = useRecoilState(LoginActiveView);
  const location = useLocation();
  const setQuestionSearch = useSetRecoilState(SearchState);
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const userDropdownRef = useRef(null);

  const isLoginPage =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/sign-in" ||
    location.pathname === "/sheet" ||
    location.pathname === "/candidate-login" ||
    location.pathname === "/404";

  useEffect(() => {
    // Filter menu items based on role and set to state
    const filteredMenu = MenuItems.filter((item) =>
      item?.roles?.includes(roleState),
    ).sort((a, b) => a.orderNo - b.orderNo);

    setMenu(filteredMenu);
  }, [roleState]); // Add roleState as dependency

  useEffect(() => {
    // Function to check screen size
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 992);
    };

    // Initial check
    checkScreenSize();

    // Add event listener to handle window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  const handleLogout = async () => {
    var response = await callApi(
      `/logout/user`,
      "PUT",
      null,
      false,
      true,
      setAlertMessage,
      navigate,
      setloginState,
    );
    if (response?.success) {
      setRoleState("");
      setloginState(false);
      setLoginActiveView("login");
      navigate("/");
    }
  };

  const renderDropdownMenu = () => (
    <>
      {!loginState ? (
        <li>
          <span
            className="dropdown-item cursor-pointer"
            onClick={() => {
              closeUserDropdown();
              navigate("/");
            }}
          >
            <FontAwesomeIcon icon={faUserDoctor} className="me-2" />
            Login
          </span>
        </li>
      ) : (
        <>
          <li>
            <span
              className="dropdown-item cursor-pointer"
              onClick={() => {
                closeUserDropdown();
                handleChangePassword();
              }}
            >
              <FontAwesomeIcon icon={faUser} className="me-2" /> Change Password
            </span>
          </li>

          <li>
            <span
              className="dropdown-item cursor-pointer"
              onClick={(e) => {
                closeUserDropdown();
                handleLogout(e);
              }}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Logout
            </span>
          </li>
        </>
      )}
    </>
  );

  const clearCacheExceptToken = () => {
    const authToken = Cookies.get("token");
    if (authToken) {
      Cookies.set("token", authToken, {
        expires: 1,
        secure: true,
        sameSite: "Strict",
      });
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = (itemId) => {
    if (activeDropdown === itemId) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(itemId);
    }
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setActiveDropdown(null);
  };

  const renderMenu = (menuItems) => {
    return (
      <ul
        className="navbar-nav position-relative"
        style={{ marginLeft: isSmallScreen ? "0%" : "20%" }}
      >
        {menuItems.map((item, index) => (
          <li
            className="nav-item dropdown position-static py-lg-2 me-lg-n1 me-xl-0"
            key={index}
          >
            {item.url ? (
              <span
                className="nav-link cursor-pointer"
                onClick={() => {
                  navigate(item.url);
                  closeAllDropdowns();
                }}
              >
                {item.moduleName}
              </span>
            ) : (
              <div className="position-relative d-inline-block">
                <span
                  className="nav-link cursor-pointer dropdown-toggle"
                  onClick={() => toggleDropdown(item.id)}
                >
                  {item.moduleName}
                </span>
                {item?.subMenu && renderSubMenu(item)}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const renderSubMenu = (parentItem) => {
    // Filter submenu items based on role
    const filteredSubMenu = parentItem.subMenu
      .filter((item) => item?.roles?.includes(roleState))
      .sort((a, b) => a.orderNo - b.orderNo);

    if (filteredSubMenu.length === 0) return null;

    const isOpen = activeDropdown === parentItem.id;

    return (
      <div
        className={`dropdown-menu rounded-4 ${isOpen ? "show" : ""}`}
        style={{
          left: "auto",
          backgroundColor: "#F5F7F8",
          position: "absolute",
          display: isOpen ? "block" : "none",
          zIndex: 1000,
        }}
      >
        <div className="d-flex flex-column gap-2">
          {filteredSubMenu.map((item, index) => (
            <span
              key={index}
              className="nav-link animate-underline animate-target fw-normal text-truncate p-0 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                navigate(item.url);
                closeAllDropdowns();
              }}
            >
              {item.moduleName}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Mobile submenu rendering
  const renderMobileSubMenu = (parentItem) => {
    const filteredSubMenu = parentItem.subMenu
      .filter((item) => item?.roles?.includes(roleState))
      .sort((a, b) => a.orderNo - b.orderNo);

    if (filteredSubMenu.length === 0) return null;

    const isOpen = activeDropdown === parentItem.id;

    return (
      <div
        className="ms-3"
        style={{
          display: isOpen ? "block" : "none",
          borderLeft: "2px solid #dee2e6",
          paddingLeft: "1rem",
        }}
      >
        {filteredSubMenu.map((item, index) => (
          <span
            key={index}
            className="nav-link cursor-pointer d-block py-2"
            onClick={() => {
              navigate(item.url);
              closeAllDropdowns();
              // Close offcanvas on mobile after navigation
              if (isSmallScreen) {
                const offcanvas = document.getElementById("navbarOffcanvas");
                if (offcanvas) {
                  const bsOffcanvas =
                    window.bootstrap?.Offcanvas?.getInstance(offcanvas);
                  if (bsOffcanvas) bsOffcanvas.hide();
                }
              }
            }}
          >
            {item.moduleName}
          </span>
        ))}
      </div>
    );
  };

  const handleChangePassword = () => {
    setLoginActiveView("change-password");
    navigate("/change-password");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".nav-item.dropdown")) {
        closeAllDropdowns();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const closeUserDropdown = () => {
    setOpen(false);
  };

  return (
    <header
      className="navbar navbar-expand-lg bg-body sticky-top"
      style={{ padding: "0px", height: "70px", marginBottom: "0.5%" }}
    >
      <div className="container py-3 d-flex justify-content-between align-items-center">
        <span
          className="navbar-brand cursor-pointer"
          onClick={() => {
            clearCacheExceptToken();
            navigate("/");
            closeAllDropdowns();
          }}
        >
          <img src={LOGO} alt="Profices" style={{ height: "40px" }} />
        </span>

        {!isLoginPage && (
          <button
            type="button"
            className="navbar-toggler"
            data-bs-toggle="offcanvas"
            data-bs-target="#navbarOffcanvas"
            aria-controls="navbarOffcanvas"
            aria-expanded="false"
            aria-label="Toggle navigation"
            onClick={closeAllDropdowns}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        )}

        <div
          className="offcanvas offcanvas-end"
          id="navbarOffcanvas"
          tabIndex="-1"
          aria-labelledby="navbarOffcanvasLabel"
        >
          <div className="offcanvas-header">
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={closeAllDropdowns}
            ></button>
          </div>
          <div className="offcanvas-body d-flex justify-content-between">
            {/* <pre>{JSON.stringify(user, null, 2)}</pre> */}
            {loginState && menu.length > 0 && (
              <>
                {isSmallScreen ? (
                  <div className="w-100">
                    {menu.map((item, index) => (
                      <div key={index} className="mb-3">
                        {item.url ? (
                          <span
                            className="nav-link cursor-pointer fs-5"
                            onClick={() => {
                              navigate(item.url);
                              closeAllDropdowns();
                              // Close offcanvas on mobile
                              const offcanvas =
                                document.getElementById("navbarOffcanvas");
                              if (offcanvas) {
                                const bsOffcanvas =
                                  window.bootstrap?.Offcanvas?.getInstance(
                                    offcanvas,
                                  );
                                if (bsOffcanvas) bsOffcanvas.hide();
                              }
                            }}
                          >
                            {item.moduleName}
                          </span>
                        ) : (
                          <>
                            <div
                              className="fw-bold fs-5 mb-2 cursor-pointer d-flex justify-content-between align-items-center"
                              onClick={() => toggleDropdown(item.id)}
                            >
                              {item.moduleName}
                              <span>
                                {item.subMenu &&
                                  (activeDropdown === item.id ? "▲" : "▼")}
                              </span>
                            </div>
                            {item.subMenu && renderMobileSubMenu(item)}
                          </>
                        )}
                      </div>
                    ))}

                    {/* User dropdown for mobile */}
                    <div className="mt-4 pt-4 border-top">
                      <ul className="list-unstyled">{renderDropdownMenu()}</ul>
                    </div>
                  </div>
                ) : (
                  <>{user?.hasPasswordChanged && renderMenu(menu)}</>
                )}
              </>
            )}

            {!isSmallScreen && !isLoginPage && loginState && (
              <ul className="navbar-nav ms-auto align-items-center">
                <li className="nav-item dropdown" ref={userDropdownRef}>
                  <button
                    className="nav-link dropdown-toggle bg-transparent border-0 d-flex align-items-center"
                    onClick={() => {
                      setOpen(!open);
                      closeAllDropdowns();
                    }}
                    aria-expanded={open}
                    style={{ fontSize: "16px" }}
                  >
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    <span className="me-2">{user?.username}</span>
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end ${open ? "show" : ""}`}
                    style={{ minWidth: "200px" }}
                  >
                    {renderDropdownMenu()}
                  </ul>
                </li>
              </ul>
            )}

            {location.pathname === "/sheet" && (
              <ul className="navbar-nav ms-auto align-items-center">
                <li className="nav-item">
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      navigate("/candidate-login");
                      closeAllDropdowns();
                    }}
                  >
                    Logout
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
